// System directives

"use client";

// System dependencies

import React, { useEffect } from "react";

// Application dependencies

import useMfaCookie from "@/_auth/hooks/useMfaCookie";
import { AuthStatus, AuthTotpStatus } from "../interfaces/auth.interfaces";
import { CORE_PRODUCT } from "@/interfaces/authorisation/authorisation.interfaces";
import {
  authSetDisplayWarning,
  authSetError,
  authSetHasVerifiedFactor,
  authSetProvider,
  authSetStatus,
  authSetUserTotpWarning,
} from "@/_auth/redux/auth/auth.slices";
import { devConsole } from "@/lib/devConsole";
import { formatErrorMessage, resetUserState } from "@/lib/functions";
import { getAuthUserById } from "@/_auth/redux/auth/auth.thunk";
import { mapProviderToEnum, resetAuthState } from "@/_auth/lib/auth.functions";
import { supabaseClient } from "@/_auth/utils/supabase/client";
import { useAppDispatch } from "@/redux/store.hooks";
import { userGetUserByEmail } from "@/redux/user/user.thunk";

// Props

interface SupabaseProviderProps {
  children: React.ReactNode;
}

// Namespace

const NAMESPACE = "SupabaseProvider";

//devConsole(namespace, ``);

const SupabaseProvider: React.FC<SupabaseProviderProps> = ({ children }) => {
  // Application state

  const { deleteCookie } = useMfaCookie();

  // Redux state

  const dispatch = useAppDispatch();

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Use effect that provides the initialisation of the auth and user state on load and the subsequent updating of the state with an
  // onAuthStateChange event.
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    // Call the load state method. This is called once on component load.

    loadState();

    // We need to register an onAuthStateChange listener for auth state changes. Once registered this will run happily until the component
    // is unloaded.

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event) => {
      handleAuthChange(event);
    });

    // Cleanup subscription on unmount

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  const resetState = async () => {
    await resetAuthState(dispatch);
    await resetUserState(dispatch);
  };

  const loadState = async () => {
    // Reset the error state.

    dispatch(authSetError(null));

    //   Try and get the current session.

    try {
      // Get the current session.

      const { data: sessionData, error: sessionError } = await supabaseClient.auth.getSession();

      // Check for an error in fetching the session.

      if (sessionError) {
        await resetState();
        return;
      }

      // If no session exists and or user exists, reset the state and return.

      if (!sessionData.session || !sessionData.session.user) {
        await resetState();
        return;
      }

      // Update the provider and status in the auth state.

      dispatch(authSetProvider(mapProviderToEnum(sessionData.session.user.app_metadata.provider)));
      dispatch(authSetStatus(AuthStatus.Authenticated));

      // Fetch the list of MFA factors for the current user.

      const { data: factorData, error: factorError } = await supabaseClient.auth.mfa.listFactors();

      // See whether we have an error to deal with.

      if (factorError) {
        // If there's an error fetching factors, clear the factor state and set a warning.

        dispatch(authSetHasVerifiedFactor(false));
        dispatch(authSetDisplayWarning(true));
        dispatch(authSetUserTotpWarning(true));
      } else {
        // Extract all factor. We are limiting our current version to TOTP specifically but will search for TOTP types in the all
        // collection.

        const allFactors = factorData.all || [];

        // Check if any verified TOTP factors exist.

        const isTotpVerified = allFactors.some((factor) => factor.status === AuthTotpStatus.Verified);

        // Update the TOTP factor state.

        dispatch(authSetHasVerifiedFactor(isTotpVerified));

        // Display warnings if necessary.

        dispatch(authSetDisplayWarning(!isTotpVerified));
        dispatch(authSetUserTotpWarning(!isTotpVerified));
      }

      // Fetch and set the auth user state.

      await dispatch(getAuthUserById({ id: sessionData.session.user.id })).unwrap();

      // Try and get the auth user email.

      const userEmail = sessionData.session.user.email;

      // Make sure it exists.

      if (!userEmail) {
        throw new Error(`Session user email is missing, can't fetch user state`);
      }

      // And set the user state.

      await dispatch(userGetUserByEmail({ email: userEmail, product: CORE_PRODUCT })).unwrap();
    } catch (error) {
      // Handle unexpected errors and reset the state.

      devConsole.error(NAMESPACE, `loadState`, `Error: ${formatErrorMessage(error)}`);
      await resetState();
    }
  };

  const handleAuthChange = async (event: string) => {
    // Reset the error state.

    dispatch(authSetError(null));

    // Switch based on the event.

    switch (event) {
      case "SIGNED_IN":
        await deleteCookie();
      case "TOKEN_REFRESHED":
        try {
          // Reload state for the signed-in user.

          await loadState();
        } catch (error) {
          // Report any untrapped errors.

          devConsole.error(NAMESPACE, `handleAuthChange`, `Error during ${event} flow: ${formatErrorMessage(error)}`);
          await supabaseClient.auth.signOut();
        }
        break;
      case "USER_UPDATED":
        // Update user-specific state only, without reloading factors.

        try {
          const { data: sessionData } = await supabaseClient.auth.getSession();
          if (sessionData?.session?.user) {
            dispatch(getAuthUserById({ id: sessionData.session.user.id }));
          }
        } catch (error) {
          devConsole.error(NAMESPACE, `handleAuthChange`, `Error updating user: ${formatErrorMessage(error)}`);
        }
        break;
      case "SIGNED_OUT":
        // Reset state on sign-out and clear the cookie if there is one.

        await deleteCookie();
        await resetState();
        break;
      default:
        break;
    }
  };

  return <>{children}</>;
};

export default SupabaseProvider;
